// Prior year hint — plan §6.4. Aynı müşterinin eski projelerinden referans.
// Kullanıcı "Bu yıl aynı" diye tek tıkla kopyalayabilir.
// M15 E1 — değer artık paylaşılan formatFormValue ile okunur özet: tekli/çoklu
// seçimde ETİKET görünür ("(yapılandırılmış)" değil), composite parça başlıklarıyla.
// Finar rev-B 19 — metin tek satıra kırpılmaz: satır sonları korunur (`pre-wrap`), uzun kelime /
// tablo satırı kırılır (`overflow-wrap:anywhere`) → yan kaydırma yok; ~300 karakterden uzun özet
// önce kısaltılır, "devamını gör" satır içinde açar. Müşteri formunda kutu yok (danışman yüzeyi).

import { useState } from "react";
import { formatFormValue, type PriorYearEntry, type QuestionSnapshot } from "@mahir/shared";
import { PERIOD_COPY, priorYearHintCopy } from "../../lib/period-ui";
import { priorHintPreview } from "../../lib/prior-year-hint-text";

interface Props {
  entry: PriorYearEntry;
  question: QuestionSnapshot;
  onCopy: () => void;
  /**
   * 13A/D10 (Geçmiş Rapor Aktarımı) — kartta KÖKEN satırı varken öneri tek satır
   * linke daralır; tıklayınca tam kart açılır. Varsayılan `false` → aktarılmamış
   * projelerde markup BİREBİR eskisi gibi kalır (e2e 11/13 metinleri R17).
   */
  collapsed?: boolean;
}

export function PriorYearHint({ entry, question, onCopy, collapsed = false }: Props): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const summary = formatFormValue(
    entry.value,
    { code: question.code, type: question.type, options: question.options },
    { emptyText: "—" },
  );
  // 2D — yıllıkta metinler AYNEN korunur (e2e spec 11 regex'i bunlara bağlı);
  // çeyreklikte "Geçen yılın aynı dönemi (2024 Q2)" kısa formu kullanılır.
  const copy = priorYearHintCopy(entry.reportingYear, entry.periodType);
  const preview = priorHintPreview(summary);

  if (collapsed && !expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="mt-2"
        aria-expanded={false}
        style={{
          display: "block",
          background: "none",
          border: "none",
          padding: 0,
          fontSize: "0.78rem",
          color: "var(--color-text-2)",
          textDecoration: "underline",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {copy.collapsedLabel}
      </button>
    );
  }

  return (
    <div
      className="mt-2 px-3 py-2 rounded-lg text-sm"
      style={{
        background: "color-mix(in srgb, var(--color-warning) 8%, white)",
        border: "1px solid color-mix(in srgb, var(--color-warning) 35%, white)",
        color: "var(--color-text-2)",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0" style={{ flex: "1 1 auto" }}>
          <div style={{ fontSize: "0.72rem", color: "var(--color-text-3)" }}>{copy.title}</div>
          <div
            data-testid="prior-year-hint-value"
            style={{ fontSize: "0.82rem", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
          >
            {preview.truncated && !showAll ? preview.text : summary}
          </div>
          {preview.truncated ? (
            <button
              type="button"
              aria-expanded={showAll}
              onClick={() => setShowAll((v) => !v)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                fontSize: "0.78rem",
                color: "var(--color-primary)",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {showAll ? PERIOD_COPY.priorShowLess : PERIOD_COPY.priorShowMore}
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onCopy}
          className="px-2 py-1 rounded text-xs"
          style={{
            background: "transparent",
            border: "1px solid color-mix(in srgb, var(--color-warning) 35%, white)",
            color: "var(--color-text-2)",
            whiteSpace: "nowrap",
          }}
        >
          {copy.copyLabel}
        </button>
      </div>
    </div>
  );
}
